import { useEffect, useState } from "react";
import { friendlyError } from "../../analytics";
import { trackApiKeyCreated } from "../../analytics/tracking";
import { useIdentityClient } from "../../identity";
import { logger } from "../../services/telemetry";
import { useWorkspace } from "../../workspaces";
import { useSnippetContext } from "../docs/snippetContext";

type Minted = { value: string; workspaceId: string };

/* De-dupes concurrent key mints across component instances. React StrictMode mounts a
 * component twice in dev (each with fresh refs/state), so a per-mount guard alone can't
 * stop a second createApiKey call — both mounts would fire before either finishes. Keyed
 * by workspace+agent, this shares ONE in-flight request between them. Cleared once settled
 * so a later, deliberate mount can mint a fresh key. */
const inFlightMints = new Map<string, Promise<Minted>>();

function mintOnce(key: string, create: () => Promise<Minted>): Promise<Minted> {
  const existing = inFlightMints.get(key);
  if (existing) return existing;

  const p = create().finally(() => {
    inFlightMints.delete(key);
  });
  inFlightMints.set(key, p);
  return p;
}

export type SnippetKeyState = "minting" | "ready" | "error";

/** Makes sure the snippet has a key: reuses one already in snippet state, otherwise mints
 *  one on mount. A key value is only returned once, at creation, so this is the only way
 *  to show a complete snippet for an agent that was set up without one. */
export function useSnippetKey(
  agentId: string,
  keyName: string,
): SnippetKeyState {
  const { workspace } = useWorkspace();
  const identity = useIdentityClient();
  const { ai21Key, setAi21Key } = useSnippetContext();

  const hasKey = ai21Key !== null;
  const [state, setState] = useState<SnippetKeyState>(
    hasKey ? "ready" : "minting",
  );

  useEffect(() => {
    if (hasKey) return;

    let cancelled = false;
    setState("minting");
    mintOnce(`${workspace.id}:${agentId}`, () =>
      identity.createApiKey(workspace.id, keyName),
    )
      .then((key) => {
        if (cancelled) return;

        trackApiKeyCreated(key.workspaceId);
        setAi21Key(key.value);
        setState("ready");
      })
      .catch((err) => {
        if (cancelled) return;

        logger.error(
          err instanceof Error ? err : new Error(friendlyError(err).message),
          "snippet key mint failed",
        );
        setState("error");
      });
    return () => {
      cancelled = true;
    };
    // Once per mount; agentId/workspace are stable for the caller's life.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}
